import React from "react";

import { ILocation, IRetailStoreWithDistance } from "../../../models/location";
import { ISyncStoreID } from "../../../models/nominals";
import { isLatLngLocation } from "../../../utils/guards";
import { getGoogleLatLng, loadGMaps, newMap } from "../../../utils/maps";

const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = {
    lat: 37.0902,
    lng: -95.7129,
};
const DEFAULT_ZOOM = 13;

interface MapState {
    map: google.maps.Map | undefined;
    info: google.maps.InfoWindow | undefined;
    markers: Map<ISyncStoreID, google.maps.Marker>;
}

const panToDefaultCenter = (
    state: MapState,
    defaultCenter?: ILocation | null,
): void => {
    if (state.map && defaultCenter && isLatLngLocation(defaultCenter)) {
        state.map.panTo(getGoogleLatLng(defaultCenter));
    }
};

const activateMarker = (
    state: MapState,
    stores: ReadonlyArray<IRetailStoreWithDistance>,
    storeID: ISyncStoreID,
): void => {
    const store = stores.find((s) => s.external_id === storeID);
    if (store && store.lat && store.lng && state.map) {
        state.map.panTo(
            getGoogleLatLng({
                lat: store.lat,
                lng: store.lng,
            }),
        );
    }
    const marker = state.markers.get(storeID);
    if (marker) {
        google.maps.event.trigger(marker, "click");
    }
};

const buildMarker = (
    state: MapState,
    store: IRetailStoreWithDistance,
    markerOptions?: (
        store: IRetailStoreWithDistance,
    ) => Partial<google.maps.MarkerOptions>,
    onBeforeInfoWindowOpen?: (
        info: google.maps.InfoWindow,
        store: IRetailStoreWithDistance,
    ) => void,
): google.maps.Marker | null => {
    if (!store.lat || !store.lng) {
        return null;
    }
    let marker = state.markers.get(store.external_id);
    if (marker) {
        return marker;
    }
    marker = new google.maps.Marker({
        position: getGoogleLatLng({
            lat: store.lat,
            lng: store.lng,
        }),
        title: store.name,
        // Add in custom marker options
        ...(markerOptions ? markerOptions(store) : {}),
    });
    marker.addListener("click", () => {
        if (!state.info) {
            return;
        }
        if (onBeforeInfoWindowOpen) {
            onBeforeInfoWindowOpen(state.info, store);
        }
        state.info.open(state.map, marker);
    });
    state.markers.set(store.external_id, marker);
    return marker;
};

const renderMarkers = (
    state: MapState,
    stores: ReadonlyArray<IRetailStoreWithDistance>,
    markerOptions?: (
        store: IRetailStoreWithDistance,
    ) => Partial<google.maps.MarkerOptions>,
    onBeforeInfoWindowOpen?: (
        info: google.maps.InfoWindow,
        store: IRetailStoreWithDistance,
    ) => void,
): void => {
    // Get map boundaries
    const mapBounds = state.map ? state.map.getBounds() : null;
    stores.forEach((store) => {
        if (!store.lat || !store.lng) {
            return;
        }
        // See if there's a cached marker already built
        const cachedMarker = state.markers.get(store.external_id);
        // Get store location
        const storeLocation = getGoogleLatLng({
            lat: store.lat,
            lng: store.lng,
        });
        // If marker is in map boundaries, add it to the map
        // Else, if theres a cached marker, remove it from the map
        if (state.map && mapBounds && mapBounds.contains(storeLocation)) {
            const marker = buildMarker(
                state,
                store,
                markerOptions,
                onBeforeInfoWindowOpen,
            );
            if (marker) {
                marker.setMap(state.map);
            }
        } else if (cachedMarker) {
            cachedMarker.setMap(null);
        }
    });
};

export interface MarkerMapHandle {
    onActivateMarker: (storeID: ISyncStoreID) => void;
}

interface MarkerMapProps {
    stores: ReadonlyArray<IRetailStoreWithDistance>;
    defaultCenter?: ILocation | null;
    elemProps?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    >;
    mapOptions?: () => google.maps.MapOptions;
    markerOptions?: (
        store: IRetailStoreWithDistance,
    ) => Partial<google.maps.MarkerOptions>;
    onBeforeInfoWindowOpen?: (
        info: google.maps.InfoWindow,
        store: IRetailStoreWithDistance,
    ) => void;
    onInfoWindowClose?: () => void;
    ref?: React.Ref<MarkerMapHandle>;
}

export const MarkerMap = (props: MarkerMapProps): React.ReactNode => {
    const { ref, ...otherProps } = props;
    const mapElem = React.useRef<HTMLDivElement>(null);
    const stateRef = React.useRef<MapState>({
        map: undefined,
        info: undefined,
        markers: new Map(),
    });

    const storesRef = React.useRef(props.stores);
    const markerOptionsRef = React.useRef(props.markerOptions);
    const beforeInfoWindowOpenRef = React.useRef(props.onBeforeInfoWindowOpen);
    const defaultCenterRef = React.useRef(props.defaultCenter);
    const infoWindowCloseRef = React.useRef(props.onInfoWindowClose);

    React.useEffect(() => {
        storesRef.current = props.stores;
    }, [props.stores]);

    React.useEffect(() => {
        markerOptionsRef.current = props.markerOptions;
    }, [props.markerOptions]);

    React.useEffect(() => {
        beforeInfoWindowOpenRef.current = props.onBeforeInfoWindowOpen;
    }, [props.onBeforeInfoWindowOpen]);

    React.useEffect(() => {
        defaultCenterRef.current = props.defaultCenter;
    }, [props.defaultCenter]);

    React.useEffect(() => {
        infoWindowCloseRef.current = props.onInfoWindowClose;
    }, [props.onInfoWindowClose]);

    // Expose onActivateMarker method to parent components
    React.useImperativeHandle(
        ref,
        () => ({
            onActivateMarker: (storeID: ISyncStoreID) => {
                activateMarker(stateRef.current, storesRef.current, storeID);
            },
        }),
        [],
    );

    // Initialize map on mount
    React.useEffect(() => {
        if (stateRef.current.map) {
            return;
        }
        const initMap = async () => {
            await loadGMaps();

            // Render map
            const mapElement = mapElem.current;
            if (!mapElement) {
                return;
            }
            stateRef.current.map = await newMap(mapElement, {
                center: DEFAULT_MAP_CENTER,
                zoom: DEFAULT_ZOOM,
                // Add in custom map options
                ...(props.mapOptions ? props.mapOptions() : {}),
            });
            stateRef.current.map.addListener("bounds_changed", () => {
                renderMarkers(
                    stateRef.current,
                    storesRef.current,
                    markerOptionsRef.current,
                    beforeInfoWindowOpenRef.current,
                );
            });

            // Initialize info window
            stateRef.current.info = new google.maps.InfoWindow({
                content: "",
            });
            stateRef.current.info.addListener("closeclick", () => {
                if (infoWindowCloseRef.current) {
                    infoWindowCloseRef.current();
                }
            });

            // Render markers
            renderMarkers(
                stateRef.current,
                storesRef.current,
                markerOptionsRef.current,
                beforeInfoWindowOpenRef.current,
            );

            // Autoselect the marker of the closest store
            if (storesRef.current.length > 0) {
                const [firstStore] = storesRef.current;
                activateMarker(
                    stateRef.current,
                    storesRef.current,
                    firstStore.external_id,
                );
            } else {
                panToDefaultCenter(stateRef.current, defaultCenterRef.current);
            }
        };

        void initMap();
    }, []);

    // Update markers when stores change
    React.useEffect(() => {
        const updateMarkers = async () => {
            await loadGMaps();
            renderMarkers(
                stateRef.current,
                storesRef.current,
                markerOptionsRef.current,
                beforeInfoWindowOpenRef.current,
            );
            // Autoselect the marker of the closest store
            if (storesRef.current.length > 0) {
                const [firstStore] = storesRef.current;
                activateMarker(
                    stateRef.current,
                    storesRef.current,
                    firstStore.external_id,
                );
            } else {
                panToDefaultCenter(stateRef.current, defaultCenterRef.current);
            }
        };

        // Only run this effect if we have a map already initialized
        if (stateRef.current.map) {
            void updateMarkers();
        }
    }, [props.stores]);

    return <div ref={mapElem} {...otherProps.elemProps} />;
};
